import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchAPI } from "../../utils/api";
import {
    Box, CircularProgress, Container, Typography, Snackbar, Alert
} from "@mui/material";

// Icons
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Interfaces & Helpers
import { Currency } from "../interfaces/OperatorDashboard";

// Sous-composants
import { DashboardHeader } from "./DashboardHeader";
import { GlobalScWalletSummary } from "./GlobalScWalletSummary";
import { OperatorTabs } from "./OperatorTabs";
import { OperatorDetailsView } from "./operator-details/OperatorDetailsView";
import { TransactionsSection } from "./TransactionsSection";
import { PendingPayoutInvoicesTable } from "./pending-invoices/PendingPayoutInvoicesTable";
import { TransactionLifecycleGraph } from "./TransactionLifecycleGraph";

export default function OperatorDashboardTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const institutionId = 'LEMONWAY';

    const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>([]);
    const [currency, setCurrency] = useState<Currency | ''>('');
    
    const [dashboardData, setDashboardData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' });

    const [selectedTab, setSelectedTab] = useState<string>(''); 

// GET CURRENCIES
    useEffect(() => {
        fetchAPI("/operators/currency")
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch currencies");
                return res.json();
            })
            .then((currencies: string[]) => {
                const filteredCurrencies = currencies.filter(curr => curr !== "ALL");
                setAvailableCurrencies(filteredCurrencies as Currency[]);
            })
            .catch(err => {
                setSnackbar({ open: true, message: `Currency loading error: ${err.message}`, severity: 'error' });
            });
    }, []);


    // GET DASHBOARD
    const getDashboard = useCallback(async (curr: string, force = false) => {
        if (!curr) return;
        setIsLoading(true);
        const dashboardUrl = `/operators/${curr}/infos${force ? '?forceRefresh=true' : ''}`;
        
        try {
            const res = await fetchAPI(dashboardUrl, { headers: { 'institution': institutionId } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
            
            setDashboardData(data);
            if (data.operators?.length > 0) setSelectedTab(data.operators[0]);
            if (force) setSnackbar({ open: true, message: 'Data refreshed', severity: 'success' });
            setSearchParams({ currency: curr });
        } catch (err: any) {
            setDashboardData(null);
            setSnackbar({ open: true, message: err.message, severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [institutionId, setSearchParams]);

    // --- SYNCHRONISATION URL <-> DASHBOARD ---
    useEffect(() => {
        const urlCurr = searchParams.get('currency') as Currency;
        
        if (availableCurrencies.length > 0 && urlCurr && availableCurrencies.includes(urlCurr)) {
            if (dashboardData?.currency !== urlCurr) {
                setCurrency(urlCurr);
                getDashboard(urlCurr, false);
            }
        }
    }, [searchParams, availableCurrencies, dashboardData?.currency, getDashboard]);

    const getVal = useCallback((mapName: string, op: string) => {
        const val = dashboardData?.[mapName]?.[op];
        return val?.total_awaited ?? val?.totalAwaited ?? val ?? 0;
    }, [dashboardData]);

    const getDetails = useCallback((mapName: string, op: string) => {
        return dashboardData?.[mapName]?.[op];
    }, [dashboardData]);

    // --- CALCULS GLOBAUX ---
    const scBalance = dashboardData?.sc_balance === -1 ? 0 : (dashboardData?.sc_balance || 0);
    const isScApiError = dashboardData?.sc_balance === -1;

    const totalAwaitedPayout = useMemo(() => {
        if (!dashboardData?.amount_awaited_payout_to_operator) return 0;
        return Object.values(dashboardData.amount_awaited_payout_to_operator).reduce((acc: number, val: any) => acc + (val?.total_awaited ?? val?.totalAwaited ?? val), 0) as number;
    }, [dashboardData]);

    const totalScCredits = useMemo(() => {
        if (!dashboardData) return 0;
        const fromEscrow = Object.values(dashboardData.amount_awaited_transfer_from_escrow_to_operator || {}).reduce((acc: number, val: any) => acc + (val?.total_awaited ?? val?.totalAwaited ?? val), 0) as number;
        const fromProd = Object.values(dashboardData.amount_awaited_transfer_from_producer_to_operator || {}).reduce((acc: number, val: any) => acc + (val?.total_awaited ?? val?.totalAwaited ?? val), 0) as number;
        return fromEscrow + fromProd;
    }, [dashboardData]);

    const projectedScBalance = scBalance + totalScCredits - totalAwaitedPayout;
    const isScRisk = projectedScBalance < 0;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            
            <DashboardHeader 
                currency={currency} 
                setCurrency={setCurrency} 
                availableCurrencies={availableCurrencies} 
                isLoading={isLoading} 
                onRefresh={(curr, force) => getDashboard(curr, force)} 
                hasData={!!dashboardData} 
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }} color="text.secondary">Fetching live data...</Typography>
                </Box>
            ) : dashboardData ? (
                <Box>
                    <GlobalScWalletSummary 
                        currency={dashboardData.currency}
                        scBalance={scBalance}
                        isScApiError={isScApiError}
                        totalScCredits={totalScCredits}
                        totalAwaitedPayout={totalAwaitedPayout}
                        projectedScBalance={projectedScBalance}
                        isScRisk={isScRisk}
                    />

                    <OperatorTabs 
                        operators={dashboardData.operators} 
                        selectedTab={selectedTab} 
                        setSelectedTab={setSelectedTab} 
                    />

                    <TransactionLifecycleGraph/>

                    <Box sx={{ display: selectedTab === 'SC_GLOBAL' ? 'block' : 'none' }}>
                        {dashboardData?.currency && (
                            <PendingPayoutInvoicesTable 
                                currency={dashboardData.currency as Currency} 
                                institutionId={institutionId} 
                            />
                        )}
                    </Box>

                    {selectedTab && selectedTab !== 'SC_GLOBAL' && (
                        <OperatorDetailsView 
                            selectedTab={selectedTab}
                            dashboardData={dashboardData}
                            scBalance={scBalance}
                            getVal={getVal}
                            getDetails={getDetails}
                        />
                    )}

                    {selectedTab && (
                        <TransactionsSection 
                            dashboardData={dashboardData}
                            selectedTab={selectedTab}
                        />
                    )}

                </Box>
            ) : (
                <Box sx={{ py: 20, textAlign: 'center', color: 'text.disabled' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 60, mb: 2, opacity: 0.2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Select a Currency</Typography>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
}